import { Observable, of } from "rxjs";
import { mergeMap, map, catchError } from "rxjs/operators";
import { ofType, Epic } from "redux-observable";

import { Action } from "@ctra/utils";
import { CardResponse, LayoutResponse, PageResponse } from "./typings";

import { makeAzureApiURL } from "../../utils/ajax";
import types from "./types";
import actions from "./actions";
import * as _ from "lodash";

/**
 * Fetch saved charts
 * @param action$
 * @param state$
 * @param Request
 */
const fetchSavedCharts: Epic = (action$, state$, { Request }) =>
  action$.pipe(
    ofType(types.FETCH_SAVED_CHARTS.pending),
    mergeMap<ReturnType<typeof actions.fetchSavedCharts.start>, Observable<Promise<unknown>>>(
      ({ payload: { contextID } }) =>
        Request.GET(
          makeAzureApiURL(
            "accounts",
            "/self/pages/<%= contextID %>"
          )({
            contextID
          })
        ).pipe(
          map<{ response: LayoutResponse }, Action>(({ response }) =>
            actions.fetchSavedCharts.fulfill(contextID, response)
          ),
          catchError<unknown, Observable<Action>>((response) => {
            const error = _.get(response, ["error"]);
            const statusCode = _.get(response, ["statusCode"]);

            return of(actions.fetchSavedCharts.reject(contextID, error, statusCode));
          })
        )
    )
  );

/**
 * Add new card
 * @param action$
 * @param state$
 * @param Request
 */
const addCard: Epic = (action$, state$, { Request }) =>
  action$.pipe(
    ofType(types.ADD_CARD.pending),
    mergeMap<ReturnType<typeof actions.addCard.start>, Observable<Promise<unknown>>>(
      ({ payload: { contextID, pageID, groupName, cardPayload } }) =>
        Request.POST(
          makeAzureApiURL(
            "accounts",
            `/self/pages/<%= contextID %>/cards?tabId=<%= pageID %>&tabName=<%= groupName %>`
          )({
            contextID,
            pageID,
            groupName
          }),
          {
            body: cardPayload
          }
        ).pipe(
          map<{ response: CardResponse }, Action>(({ response }) => {
            return actions.addCard.fulfill(contextID, pageID, response);
          }),
          catchError<unknown, Observable<Action>>((response) => {
            const error = _.get(response, ["error"]);
            const statusCode = _.get(response, ["statusCode"]);

            return of(actions.addCard.reject(error, statusCode));
          })
        )
    )
  );

/**
 * Add new tab
 * @param action$
 * @param state$
 * @param Request
 */
const addTab: Epic = (action$, state$, { Request }) =>
  action$.pipe(
    ofType(types.ADD_TAB.pending),
    mergeMap<ReturnType<typeof actions.addTab.start>, Observable<Promise<unknown>>>(
      ({ payload: { contextID, groupName } }) =>
        Request.POST(
          makeAzureApiURL(
            "accounts",
            `/self/pages/<%= contextID %>/tabs`
          )({
            contextID
          }),
          {
            body: { name: groupName, cards: [] }
          }
        ).pipe(
          map<{ response: PageResponse }, Action>(({ response }) => {
            return actions.addTab.fulfill(contextID, response);
          }),
          catchError<unknown, Observable<Action>>((response) => {
            const error = _.get(response, ["error"]);
            const statusCode = _.get(response, ["statusCode"]);

            return of(actions.addTab.reject(error, statusCode));
          })
        )
    )
  );

/**
 * Update tabs (add/remove/reorder)
 * @param action$
 * @param state$
 * @param Request
 */
const updateTabs: Epic = (action$, state$, { Request }) =>
  action$.pipe(
    ofType(types.UPDATE_TABS.pending),
    mergeMap<ReturnType<typeof actions.updateTabs.start>, Observable<Promise<unknown>>>(
      ({ payload: { contextID, pages } }) => {
        return Request.PUT(
          makeAzureApiURL(
            "accounts",
            `/self/pages/<%= contextID %>`
          )({
            contextID
          }),
          {
            body: pages
          }
        ).pipe(
          map<{ response: LayoutResponse }, Action>((request) => {
            return actions.updateTabs.fulfill(contextID, request.response);
          }),
          catchError<unknown, Observable<Action>>((response) => {
            const error = _.get(response, ["error"]);
            const statusCode = _.get(response, ["statusCode"]);

            return of(actions.updateTabs.reject(error, statusCode));
          })
        );
      }
    )
  );

/**
 * Save chart to group/tab
 * @param action$
 * @param state$
 * @param Request
 */
const updateCharts: Epic = (action$, state$, { Request }) =>
  action$.pipe(
    ofType(types.UPDATE_CHARTS.pending),
    mergeMap<ReturnType<typeof actions.updateCharts.start>, Observable<Promise<unknown>>>(
      ({ payload: { contextID, pages } }) => {
        return Request.PUT(
          makeAzureApiURL(
            "accounts",
            `/self/pages/<%= contextID %>`
          )({
            contextID
          }),
          {
            body: pages
          }
        ).pipe(
          map<{ response: LayoutResponse }, Action>((request) => {
            return actions.updateCharts.fulfill(contextID, request.response);
          }),
          catchError<unknown, Observable<Action>>((response) => {
            const error = _.get(response, ["error"]);
            const statusCode = _.get(response, ["statusCode"]);

            return of(actions.updateCharts.reject(error, statusCode));
          })
        );
      }
    )
  );

export default {
  updateCharts,
  updateTabs,
  fetchSavedCharts,
  addCard,
  addTab
};
